<template>
  <v-container fluid>
    <v-card class="mx-auto mt-5" outlined tile>

      <v-row>
        <v-col class="ma-5">
          <v-expansion-panels flat v-model="expandOptions">
            <v-expansion-panel>
              <v-expansion-panel-header>Filters ({{ filterCount }})</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-form>
                  <v-row>
                    <v-col cols="12" md="6">
                      <date-input
                        label="From"
                        v-model="minDate"
                        :max="maxDate"
                        required
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <date-input
                        label="To"
                        v-model="maxDate"
                        :min="minDate"
                        required
                      />
                    </v-col>
                  </v-row>
                  <v-text-field
                    label="Establishment ID"
                    name="establishmentId"
                    v-model="filter.establishmentId"
                  />
                  <v-text-field
                    label="Grader Code"
                    v-model="filter.graderCode"
                  />
                  <v-text-field
                    label="Organisation Name"
                    v-model="filter.organisationName"
                  />
                  <v-text-field
                    label="Course/Module Code"
                    v-model="filter.courseCode"
                  />
                  <v-text-field
                    label="Unit Code"
                    v-model="filter.unitCode"
                  />
                </v-form>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <v-data-table
        dense
        disable-filtering
        disable-sort
        :headers="this.headers"
        :options.sync="options"
        :footer-props="footerProps"
        :server-items-length="this.list.count"
        :items="this.list.items"
        :loading="$apollo.queries.list.loading"
        item-key="contactID"
      >
        <template v-slot:[`item.dateOfBirth`]="{ value }">
          {{ value | formatDate }}
        </template>

        <template v-slot:[`item.modules`]="{ item }">
          {{ getTraineeModuleUnits(item) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn small icon @click="showItem(item)">
            <v-icon color="primary">info</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="dialog"
      width="1200"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">
          {{ selectedItem.givenName }} {{ selectedItem.surname }}
          <span v-show="!!selectedItem.usi">
            &dash; {{ selectedItem.usi }}
          </span>
        </v-card-title>

        <v-data-table
          dense
          disable-pagination
          hide-default-footer
          show-expand
          :headers="enrolmentHeaders"
          :items="selectedItem.trainingClasses"
          :expanded.sync="enrolmentExpanded"
          item-key="enrolmentId"
        >
          <template v-slot:[`item.classStartDate`]="{ value }">
            {{ value | formatDate }}
          </template>
          <template v-slot:[`item.classFinishDate`]="{ value }">
            {{ value | formatDate }}
          </template>

          <template v-slot:expanded-item="{ item }">
            <td :colspan="enrolmentHeaders.length" class="pa-2">
              <v-simple-table
                dense
                class="mb-5"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th width="30%" class="text-left">
                        Unit
                      </th>
                      <th width="10%" class="text-left">
                        Outcome Code
                      </th>
                      <th width="20%" class="text-left">
                        Outcome Description
                      </th>
                      <th width="20%" class="text-left">
                        Trainer
                      </th>
                      <th width="20%" class="text-end">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="unit in item.trainingUnits"
                      :key="unit.enrolmentCompetencyUnitID"
                    >
                      <td>{{ unit.unitCode }} &dash; {{ unit.unitName }}</td>
                      <td>{{ unit.outcomeCode }}</td>
                      <td>{{ unit.outcomeDescription }}</td>
                      <td>{{ unit.trainerName }}</td>
                      <td class="text-end">{{ unit.actualCompletionDate | formatDate }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { startOfDay, endOfDay, subYears } from 'date-fns'
import DateInput from '../components/inputs/DateInput.vue'

export default {
  name: 'AusMeatTraineeListView',

  components: {
    DateInput
  },

  data () {
    const now = new Date()
    return {
      // Trainee list
      expandOptions: 0,
      headers: [
        { text: 'AUS-MEAT Contact ID', value: 'contactID' },
        { text: 'USI', value: 'usi' },
        { text: 'Given Name', value: 'givenName' },
        { text: 'Family Name', value: 'surname' },
        { text: 'Date of Birth', value: 'dateOfBirth' },
        { text: 'Establishment ID', value: 'establishmentID' },
        { text: 'Grader Code', value: 'graderCode' },
        { text: 'Organisation ID', value: 'organisationID' },
        { text: 'Organisation Name', value: 'organisationName' },
        { text: 'Modules Attended', value: 'modules' },
        { text: '', value: 'actions', align: 'end' }
      ],
      options: {
        page: 1,
        itemsPerPage: 25
      },
      footerProps: {
        'items-per-page-options': [10, 25, 50, 100, 200, 500]
      },
      list: {
        count: 0,
        items: []
      },
      minDate: startOfDay(subYears(now, 20)).toISOString(),
      maxDate: endOfDay(now).toISOString(),
      filter: {
        establishmentId: '',
        graderCode: '',
        organisationName: '',
        courseCode: '',
        unitCode: ''
      },

      // Trainee details
      dialog: false,
      selectedItem: {
        trainingClasses: []
      },
      enrolmentHeaders: [
        { text: '', value: 'data-table-expand' },
        { text: 'Course Code', value: 'courseCode' },
        { text: 'Course Name', value: 'courseName' },
        { text: 'Start Date', value: 'classStartDate', align: 'end' },
        { text: 'Finish Date', value: 'classFinishDate', align: 'end' }
      ],
      enrolmentExpanded: []
    }
  },

  computed: {
    filterCount () {
      return Object.values(this.filter).filter(Boolean).length
    }
  },

  apollo: {
    list: {
      query: gql`query SearchAusMeatTrainees ($filter: FilterFindManyAusMeatTraineeInput, $page: Int!, $perPage: Int!, $minDate: Date! $maxDate: Date!) {
        list: ausMeatTraineeManyPagination(filter: $filter, page: $page, perPage: $perPage, minDate: $minDate, maxDate: $maxDate) {
          count
          items {
            contactID
            usi
            givenName
            surname
            dateOfBirth
            establishmentID
            organisationID
            organisationName
            graderCode
            trainingClasses {
              enrolmentId
              classDescriptor
              courseName
              courseCode
              classStartDate
              classFinishDate
              trainingUnits {
                enrolmentCompetencyUnitID
                unitName
                unitCode
                outcomeDescription
                outcomeCode
                trainerName
                actualCompletionDate
              }
            }
          }
        }
      }`,
      variables () {
        return {
          minDate: this.minDate,
          maxDate: this.maxDate,
          filter: { ...this.filter },
          page: this.options.page,
          perPage: this.options.itemsPerPage
        }
      },
      error (e) { this.$emit('error', e) },
      debounce: 500
    }
  },

  methods: {
    showItem (item) {
      this.selectedItem = item
      this.dialog = true
    },

    getTraineeModuleUnits (item) {
      const modules = new Map()
      for (const trainingClass of item.trainingClasses) {
        const moduleCode = trainingClass.courseCode
        const units = new Set(modules.get(moduleCode))
        for (const trainingUnit of trainingClass.trainingUnits) {
          const { unitCode } = trainingUnit
          if (unitCode !== moduleCode) units.add(trainingUnit.unitCode)
        }
        modules.set(moduleCode, [...units])
      }

      return Array.from(modules).map(([moduleCode, unitCodes]) => {
        return unitCodes.length
          ? `${moduleCode} (${unitCodes.join(', ')})`
          : moduleCode
      }).join(', ')
    }
  }
}
</script>
